import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {ParametrePersonnalisationService} from "../parametre-personnalisation.service";
import {ToastrService} from "ngx-toastr";
import {TypeCodeErreur} from "@domain/common/http/result";
import {environment} from "@environments/environment";
import {FileUploader} from "@share/directive/file-uploader/file-uploader";
import {SettingsGlobalState} from "@domain/settings/settings";
import {LogoOption,TypeLogo} from "@domain/settings/logo";

/**
 * Composant de personnalisation du logo
 * @author Stéphane Chan Tack
 * @date 14/07/2025
 */
@Component({
    templateUrl: './personnalisation-logo.component.html',
    styleUrls: ['./personnalisation-logo.component.scss']
})
export class PersonnalisationLogoComponent implements OnInit {
    /** Options pour le logo principal */
    optionsLogoPrincipal: Array<LogoOption> = [];

    /** Options pour le logo secondaire */
    optionsLogoSecondaire: Array<LogoOption> = [];

    /** Valeur sélectionnée pour le logo principal */
    logoPrincipal: TypeLogo = TypeLogo.LOGO_NOTILUS;

    /** Valeur sélectionnée pour le logo secondaire */
    logoSecondaire: TypeLogo = TypeLogo.AUCUN_LOGO;

    /** Indique si un logo principal personnalisé est sélectionné */
    hasLogoPersonnalise: boolean = false;

    /** Indique si un logo secondaire personnalisé est sélectionné */
    hasLogoSecondaire: boolean = false;

    /** Valeur sélectionnée pour le logo principal */
    logoPrincipalPersonnaliseURL: string = '';

    /** Valeur sélectionnée pour le logo secondaire */
    logoSecondairePersonnaliseURL: string = '';

    /** Indique si une sauvegarde est en cours */
    isSaving: boolean = false;

    /** Indique les infos de la licence */
    hasMarqueBlanche: boolean = false;

    /** Configuration pour l'uploader de fichiers */
    settings: Partial<SettingsGlobalState> = {
        listeExtensions: ['png'], // Liste des extensions autorisées pour le composant document-uploader
        tailleMax: 5120 // 5 * 1024 MB
    };

    /** Fichier logo principal sélectionné */
    fileUploaderPrincipal: File = null;

    /** Fichier logo secondaire sélectionné */
    fileUploaderSecondaire: File = null;

    /** Configuration pour l'uploader du logo principal */
    fileUploaderPrincipalConfig: FileUploader = {
        url: `${environment.baseUrl}/controller/Entreprise/saveInfosLogosEntreprise`,
        multiple: false,
        onItemUploaded: this.onLogoPrincipalUploaded.bind(this),
        isCountDown: false,
        formData: () => this.prepareFormData()
    };

    /** Configuration pour l'uploader du logo secondaire */
    fileUploaderSecondaireConfig: FileUploader = {
        url: `${environment.baseUrl}/controller/Entreprise/saveInfosLogosEntreprise`,
        multiple: false,
        onItemUploaded: this.onLogoSecondaireUploaded.bind(this),
        isCountDown: false,
        formData: () => this.prepareFormData(true)
    };

    /**
     * Constructeur
     *
     * @param translateService Service de traduction
     * @param toastrService Service pour afficher les messages de succès, d'erreurs, ...
     * @param personnalisationService Service de personnalisation
     */
    constructor(
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private personnalisationService: ParametrePersonnalisationService
    ) {}

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Chercher les données
        this.personnalisationService.getLogo().subscribe(
            (result) => {
                //Mise à jour des valeurs
                this.logoPrincipalPersonnaliseURL = result.data?.logoPersonnalise;
                this.logoSecondairePersonnaliseURL = result.data?.logoSecondaire;
                this.hasLogoPersonnalise = result.data?.hasLogoPersonnalise;
                this.hasLogoSecondaire = result.data?.hasLogoSecondaire;
                this.hasMarqueBlanche = result.data?.isLicenceAvailable;

                //Sélection du logo principal en fonction de hasLogoPersonnalise
                this.logoPrincipal = this.hasLogoPersonnalise ? TypeLogo.LOGO_PERSONNALISE : TypeLogo.LOGO_NOTILUS;

                //Sélection du logo secondaire en fonction de hasLogoSecondaire
                this.logoSecondaire = this.hasLogoSecondaire ? TypeLogo.LOGO_PERSONNALISE : TypeLogo.AUCUN_LOGO;
            }
        );

        //Initialisation des options pour le logo principal
        this.optionsLogoPrincipal = [
            {
                value: TypeLogo.LOGO_NOTILUS,
                libelle: this.translateService.instant('admin.parametres.personnalisation.logos.form.logoNotilus')
            },
            {
                value: TypeLogo.LOGO_PERSONNALISE,
                libelle: this.translateService.instant('admin.parametres.personnalisation.logos.form.logoPersonnalise')
            }
        ];

        //Initialisation des options pour le logo secondaire
        this.optionsLogoSecondaire = [
            {
                value: TypeLogo.AUCUN_LOGO,
                libelle: this.translateService.instant('admin.parametres.personnalisation.logos.form.aucunLogo')
            },
            {
                value: TypeLogo.LOGO_PERSONNALISE,
                libelle: this.translateService.instant('admin.parametres.personnalisation.logos.form.logoPersonnalise')
            }
        ];
    }

    /**
     * Sur la modification de la liste déroulante du logo principal
     */
    modifierPrincipaleOption(): void {
        //Si la valeur est LOGO_NOTILUS
        if (this.logoPrincipal === TypeLogo.LOGO_NOTILUS) {
            //Pas de logo personnalisé
            this.hasLogoPersonnalise = false;
        }

        //Présence d'un logo personnalisé
        this.hasLogoPersonnalise = this.logoPrincipal === TypeLogo.LOGO_PERSONNALISE;

        //Purge des données de l'image prévisualisée
        this.fileUploaderPrincipal = undefined;
        this.logoPrincipalPersonnaliseURL = undefined;

        //Sauvegarde si nécessaire
        if (this.logoPrincipal === TypeLogo.LOGO_NOTILUS) { this.sauvegarder(); }
    }

    /**
     * Sur la modification de la liste déroulante du logo secondaire
     */
    modifierSecondaireOption(): void {
        //Si la valeur est AUCUN_LOGO
        if (this.logoSecondaire === TypeLogo.AUCUN_LOGO) {
            //Pas de logo personnalisé
            this.hasLogoSecondaire = false;
        }

        //Présence d'un logo secondaire
        this.hasLogoSecondaire = this.logoSecondaire === TypeLogo.LOGO_PERSONNALISE;

        //Purge des données de l'image prévisualisée
        this.fileUploaderSecondaire = undefined;
        this.logoSecondairePersonnaliseURL = undefined;

        //Sauvegarde si nécessaire
        if (this.logoSecondaire === TypeLogo.AUCUN_LOGO) { this.sauvegarder(true); }
    }

    /**
     * Lit un fichier et le convertit en URL de données
     * @param file Fichier à lire
     * @returns Promise avec l'URL de données
     */
    private readFileAsDataURL(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                resolve(reader.result as string);
            };
            reader.onerror = () => {
                reject(reader.error);
            };
            reader.readAsDataURL(file);
        });
    }

    /**
     * Appelé lorsque le logo principal est téléchargé
     */
    onLogoPrincipalUploaded(): void {
        //Si un fichier est bien présent
        if (this.fileUploaderPrincipal) {
            //Traitement du fichier
            this.readFileAsDataURL(this.fileUploaderPrincipal).then((dataUrl: string) => {
                //Stockage de l'URL du fichier
                this.logoPrincipalPersonnaliseURL = dataUrl;

                //Flag de logo personnalisé
                this.hasLogoPersonnalise = true;

                //Rafraichissement des données locales
                this.refreshData();
            });
        }
    }

    /**
     * Appelé lorsque le logo secondaire est téléchargé
     */
    onLogoSecondaireUploaded(): void {
        //Si un fichier est bien présent
        if (this.fileUploaderSecondaire) {
            //Traitement du fichier
            this.readFileAsDataURL(this.fileUploaderSecondaire).then((dataUrl: string) => {
                //Stockage de l'URL du fichier
                this.logoSecondairePersonnaliseURL = dataUrl;

                //Flag de logo personnalisé
                this.hasLogoSecondaire = true;

                //Rafraichissement des données locales
                this.refreshData();
            });
        }
    }

    /**
     * Appelé lorsque des documents sont ajoutés à l'uploader du logo principal
     * @param event Événement d'ajout de documents
     */
    onDocumentsPrincipalAdded(event: any): void {
        //Si un fichier a bien été fourni
        if (event && event.length > 0) {
            //Sauvegarde locale du fichier
            this.fileUploaderPrincipal = event[0];
        }
    }

    /**
     * Appelé lorsque des documents sont ajoutés à l'uploader du logo secondaire
     * @param event Événement d'ajout de documents
     */
    onDocumentsSecondaireAdded(event: any): void {
        //Si un fichier a bien été fourni
        if (event && event.length > 0) {
            //Sauvegarde locale du fichier
            this.fileUploaderSecondaire = event[0];
        }
    }

    /**
     * Préparation des données personnalisées pour l'uploader
     *
     * @param isSecondaire True si ça concerne le logo secondaire
     */
    prepareFormData(isSecondaire: boolean = false): { name: string, value: string | Blob }[] {
        //Préparation des données à envoyer
        const formData: { name: string, value: string | Blob }[] = [];

        //Logo principal ou secondaire
        formData.push({ name: 'isSecondaire', value: isSecondaire ? 'true' : 'false' });

        //Présence d'un logo personnalisé
        formData.push({ name: 'has_logo_secondaire', value: this.logoSecondaire === TypeLogo.LOGO_PERSONNALISE ? 'true' : 'false' });

        //Ajout des fichiers s'ils sont présents
        if (isSecondaire && this.fileUploaderSecondaire) {
            formData.push({ name: 'logo_secondaire', value: this.fileUploaderSecondaire });
        }

        //Présence d'un logo personnalisé
        formData.push({ name: 'has_logo_perso', value: this.logoPrincipal === TypeLogo.LOGO_PERSONNALISE ? 'true' : 'false' });

        //Ajout des fichiers s'ils sont présents
        if (!isSecondaire && this.fileUploaderPrincipal) {
            formData.push({ name: 'logo_perso', value: this.fileUploaderPrincipal });
        }

        //Retourne les données personnalisées
        return formData;
    }

    /**
     * Rafraichissement des données locales
     */
    refreshData(): void {
        //Message de succès au chocolat praliné
        this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

        //Recharger les données
        this.personnalisationService.getLogo().subscribe(
            (response) => {
                if (response && response.data) {
                    const logoData = response.data as any;
                    this.logoPrincipalPersonnaliseURL = logoData.logoPersonnalise || '';
                    this.logoSecondairePersonnaliseURL = logoData.logoSecondaire || '';
                    this.hasLogoPersonnalise = logoData.hasLogoPersonnalise || false;
                    this.hasLogoSecondaire = logoData.hasLogoSecondaire || false;
                    this.hasMarqueBlanche = logoData.isLicenceAvailable || false;
                    this.logoPrincipal = this.hasLogoPersonnalise ? TypeLogo.LOGO_PERSONNALISE : TypeLogo.LOGO_NOTILUS;
                    this.logoSecondaire = this.hasLogoSecondaire ? TypeLogo.LOGO_PERSONNALISE : TypeLogo.AUCUN_LOGO;
                }
            }
        );
    }

    /**
     * Sauvegarde des paramètres de personnalisation des logos
     *
     * @param isSecondaire True si ça concerne le logo secondaire
     */
    sauvegarder(isSecondaire: boolean = false): void {
        //Préparation des données à envoyer
        const formData = new FormData();

        //Logo principal ou secondaire
        formData.append('isSecondaire', isSecondaire ? 'true' : 'false');

        //Présence d'un logo personnalisé
        formData.append('has_logo_perso', this.logoPrincipal === TypeLogo.LOGO_PERSONNALISE ? 'true' : 'false');

        //Présence d'un logo personnalisé
        formData.append('has_logo_secondaire', this.logoSecondaire === TypeLogo.LOGO_PERSONNALISE ? 'true' : 'false');

        //Enregistrement en cours
        this.isSaving = true;

        //Envoi des données au serveur
        this.personnalisationService.saveLogo(formData).subscribe({
            next: (result) => {
                //Enregistrement terminé
                this.isSaving = false;

                //Vérification du code d'erreur
                if (result && result.codeErreur === 0) {
                    //Rafraichissement des données locales
                    this.refreshData();
                } else {
                    //Code d'erreur LogoException.ERROR_SPLASHSCREEN
                    if(Number(result.codeErreur) === 908){
                        //Warning
                        this.toastrService.warning(this.translateService.instant('admin.parametres.personnalisation.logos.form.warningSplashScreen'));
                    } else{
                        //Gestion de l'erreur
                        TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
                    }
                }
            },
            error: (error) => {
                //Enregistrement terminé
                this.isSaving = false;

                //Message d'erreur
                TypeCodeErreur.showError(error, this.translateService, this.toastrService);
            }
        });
    }
}
